import { useEffect } from 'react';
import UrgencyBanner from './components/UrgencyBanner';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import DiagnosticSection from './components/DiagnosticSection';
import PerformanceSection from './components/PerformanceSection';
import ResultsSection from './components/ResultsSection';
import IANMediaSection from './components/IANMediaSection';
import Testimonials from './components/Testimonials';
import CTASection from './components/CTASection';
import FloatingCTA from './components/FloatingCTA';
import Footer from './components/Footer';

const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

const schemaOrganization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '이안미디어 IAN MEDIA',
  alternateName: 'IAN MEDIA',
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: 'https://static.readdy.ai/image/082ecbf3b95a41c4fe2e0b7873d9af01/8e53b62f11561aee91383179c58b2ff8.png',
  },
  description:
    '이안미디어는 데이터 기반 퍼포먼스 마케팅 전문 광고회사입니다. 광고비 효율 개선, ROAS 상승, 매출 성장을 만들어드립니다.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'KR',
    addressLocality: '서울',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    availableLanguage: 'Korean',
  },
  sameAs: [],
};

const schemaWebSite = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '이안미디어 | 퍼포먼스 마케팅 광고대행사 | ROAS 매출 상승 전문',
  url: siteUrl,
  inLanguage: 'ko-KR',
  description:
    '이안미디어는 데이터 기반 퍼포먼스 마케팅 전문 광고회사입니다. 광고비 효율 개선, ROAS 상승, 매출 성장을 만들어드립니다.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

const schemaLocalBusiness = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: '이안미디어 IAN MEDIA',
  alternateName: 'IAN MEDIA',
  url: siteUrl,
  logo: 'https://static.readdy.ai/image/082ecbf3b95a41c4fe2e0b7873d9af01/8e53b62f11561aee91383179c58b2ff8.png',
  image: 'https://public.readdy.ai/ai/img_res/f2f91f154d6a4c66e83c6409b313478d.jpg',
  description:
    '데이터 기반 퍼포먼스 마케팅 전문 광고대행사. ROAS 상승, 광고비 효율 개선, 네이버·구글 광고, 매출 성장 전략 전문.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'KR',
    addressLocality: '서울',
  },
  areaServed: {
    '@type': 'Country',
    name: 'KR',
  },
  priceRange: '₩₩₩',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: '퍼포먼스 마케팅 서비스',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '네이버 광고',
          description: '네이버 검색광고 기획, 운영, ROAS 최적화',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '구글 광고',
          description: '구글 애즈 기획, 운영, 매출 상승 최적화',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '메타·인스타그램 광고',
          description: '메타(페이스북·인스타그램) 퍼포먼스 광고 운영, 광고비 효율 개선',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '무료 광고 진단',
          description: '현재 광고 계정 무료 진단 및 매출 성장 전략 제안',
        },
      },
    ],
  },
  knowsAbout: [
    '광고대행사',
    '퍼포먼스 마케팅',
    '네이버 광고',
    '구글 광고',
    'ROAS',
    '매출 상승',
  ],
};

const schemaFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '이안미디어의 평균 ROAS는 얼마나 되나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '이안미디어는 데이터 기반 퍼포먼스 마케팅을 통해 평균 ROAS 12배 이상의 성과를 달성하고 있습니다.',
      },
    },
    {
      '@type': 'Question',
      name: '무료 광고 진단은 어떻게 신청하나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '페이지 하단의 상담 신청 폼을 작성하시면 24시간 내 연락을 드립니다. 상담 비용은 0원이며 계약 강요 없이 진단만 받으실 수 있습니다.',
      },
    },
    {
      '@type': 'Question',
      name: '이안미디어는 어떤 광고 채널을 운영하나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '네이버 검색광고, 구글 애즈, 메타(페이스북·인스타그램) 광고, 카카오 광고 등 주요 퍼포먼스 마케팅 채널을 전문적으로 운영합니다.',
      },
    },
  ],
};

function injectJsonLd(id: string, data: object) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export default function Home() {
  useEffect(() => {
    injectJsonLd('schema-organization', schemaOrganization);
    injectJsonLd('schema-website', schemaWebSite);
    injectJsonLd('schema-localbusiness', schemaLocalBusiness);
    injectJsonLd('schema-faq', schemaFAQ);

    return () => {
      ['schema-organization', 'schema-website', 'schema-localbusiness', 'schema-faq'].forEach(
        (id) => document.getElementById(id)?.remove()
      );
    };
  }, []);

  return (
    <main className="bg-black min-h-screen overflow-x-hidden w-full" style={{ fontFamily: "'Noto Sans KR', 'Space Grotesk', sans-serif" }}>
      {/* Fixed UI */}
      <UrgencyBanner />
      <Navbar />

      {/* 1. 히어로 — 문제 제기 + CTA */}
      <HeroSection />

      {/* 2. IAN MEDIA 회사 소개 — 브랜드 매니페스토 */}
      <IANMediaSection />

      {/* 3. 진단 체크리스트 — 고통 증폭 */}
      <DiagnosticSection />

      {/* 4. 퍼포먼스 마케팅 시스템 — 서비스 소개 */}
      <PerformanceSection />

      {/* 5. 결과 — 증거 */}
      <ResultsSection />

      {/* 6. 후기 — 사회적 증거 */}
      <Testimonials />

      {/* 7. 폼 — 행동 */}
      <CTASection />

      {/* Floating */}
      <FloatingCTA />
      <Footer />
    </main>
  );
}